import React, { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./contexts/LanguageContext";
import LanguageSwitcher from "./components/LanguageSwitcher";
import NotificationBell from "./components/NotificationBell";
import OnboardingForm from "./OnboardingForm";
import SoilUpload from "./SoilUpload";
import InstallPrompt from "./components/InstallPrompt";
import PlotManagement from "./PlotManagement";
import BiocharResources from "./BiocharResources";
import OffsetsPlaceholder2026 from "./OffsetsPlaceholder2026";
import CarbonDashboard from "./CarbonDashboard";
import KilnRegistration from "./components/KilnRegistration";
import BurnLogger from "./components/BurnLogger";
// import EarningsTracker from "./components/EarningsTracker"; // Hidden per Michael's request - no revenue promises

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [kpis, setKpis] = useState(null);
  const [isOffsetParticipant, setIsOffsetParticipant] = useState(false);
  const [carbonLoading, setCarbonLoading] = useState(true);

  // Biochar to Soil state
  const [kilns, setKilns] = useState([]);
  const [burns, setBurns] = useState([]);
  const [biocharStats, setBiocharStats] = useState({});
  const [biocharLoading, setBiocharLoading] = useState(true);
  const [showKilnForm, setShowKilnForm] = useState(false);
  const [showBurnForm, setShowBurnForm] = useState(false);
  const [selectedKiln, setSelectedKiln] = useState(null);
  const [selectedBurn, setSelectedBurn] = useState(null);
  const [biocharTab, setBiocharTab] = useState('overview');
  const [plotCount, setPlotCount] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetch('/api/reports')
      .then(res => res.json())
      .then(data => setKpis(data.kpis))
      .catch(err => console.error('Failed to load KPIs:', err));

    // Check if farmer is an offset participant (Track A)
    checkOffsetStatus();
    
    // Load biochar data
    fetchBiocharData();

    // Check plot count for nudge banner
    fetchPlotCount();
  }, []);

  const checkOffsetStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/farmers/carbon', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.ok && data.is_offset_participant) {
        setIsOffsetParticipant(true);
      }
    } catch (err) {
      console.error('Failed to check offset status:', err);
    } finally {
      setCarbonLoading(false);
    }
  };

  const fetchPlotCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/plots', { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setPlotCount((data.plots || []).length);
      }
    } catch (err) {
      console.error('Failed to fetch plots:', err);
    }
  };

  const fetchBiocharData = async () => {
    try {
      setBiocharLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch kilns
      const kilnRes = await fetch('/api/kilns', { headers });
      if (kilnRes.ok) {
        const kilnData = await kilnRes.json();
        setKilns(kilnData.kilns || []);
      }

      // Fetch burns
      const burnRes = await fetch('/api/burns', { headers });
      if (burnRes.ok) {
        const burnData = await burnRes.json();
        setBurns(burnData.burns || []);
      }

      // Fetch earnings
      const earningsRes = await fetch('/api/earnings', { headers });
      if (earningsRes.ok) {
        const earningsData = await earningsRes.json();
        setBiocharStats(earningsData.earnings || {});
      }
    } catch (err) {
      console.error('Error fetching biochar data:', err);
    } finally {
      setBiocharLoading(false);
    }
  };

  // Calculate biochar stats
  const completedBurns = burns.filter(b => b.status === 'complete').length;
  const totalCredits = burns.reduce((sum, b) => sum + (b.credits_earned || 0), 0);
  const verifiedKilns = kilns.filter(k => k.status === 'verified').length;

  return (
    <div style={{ minHeight: '100vh', background: '#0f2419', color: 'white', padding: '20px' }}>
      <header style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px', 
        paddingBottom: '20px', 
        borderBottom: '1px solid #2d5a45' 
      }}>
        {/* LEFT SIDE - Logo and Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <img 
            src="/agroconnect-logo.png" 
            alt="AgroConnect.io" 
            style={{ 
              height: '60px', 
              width: '60px', 
              objectFit: 'contain',
              filter: 'drop-shadow(0 0 10px rgba(74, 222, 128, 0.3))'
            }}
          />
          <div>
            <h1 style={{ margin: 0, fontSize: '28px' }}>AgroConnect.io</h1>
            <p style={{ margin: '4px 0 0 0', color: '#4ade80', fontSize: '14px', fontStyle: 'italic' }}>
              Touch. Track. Thrive.
            </p>
            <p style={{ margin: '8px 0 0 0', color: '#888', fontSize: '13px' }}>
              {t('dashboard.loggedInAs')}: {user?.email} ({user?.role})
              {isOffsetParticipant && (
                <span style={{ marginLeft: '10px', padding: '2px 8px', background: '#22c55e', color: 'white', borderRadius: '4px', fontSize: '11px' }}>
                  Offset Participant
                </span>
              )}
            </p>
            {user?.role === 'Admin' && (
              <p style={{ margin: '4px 0 0 0' }}>
                <a href="/admin" style={{ color: '#4ade80', textDecoration: 'none', fontWeight: 'bold' }}>
                  Go to Admin Dashboard
                </a>
              </p>
            )}
          </div>
        </div>

        {/* RIGHT SIDE - Actions */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <LanguageSwitcher />
          <NotificationBell />
          <button 
            onClick={() => window.location.href = '/profile'} 
            style={{ 
              padding: '10px 20px', 
              background: '#4ade80', 
              color: '#0f2419', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              fontWeight: 'bold' 
            }}
          >
            {t('profile.title')}
          </button>
          <button 
            onClick={logout} 
            style={{ 
              padding: '10px 20px', 
              background: '#ef4444', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              fontWeight: 'bold' 
            }}
          >
            {t('common.logout')}
          </button>
        </div>
      </header>

      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

        {/* STICKY QUICK ACTION BAR */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          display: 'flex',
          gap: '8px',
          marginBottom: '20px',
          padding: '10px 14px',
          background: '#1a3327',
          borderBottom: '2px solid #4ade80',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <span style={{ color: '#4ade80', fontWeight: 'bold', fontSize: '12px', marginRight: '4px' }}>
            🔥 Biochar:
          </span>
          <button
            onClick={() => setShowKilnForm(true)}
            style={{ padding: '8px 16px', background: '#4ade80', color: '#0f2419', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
          >
            + Register Kiln
          </button>
          <button
            onClick={() => {
              if (kilns.length > 0) {
                setSelectedKiln(kilns[0]);
                setSelectedBurn(null);
                setShowBurnForm(true);
              } else {
                alert('Please register a kiln first before logging a burn.');
              }
            }}
            style={{ padding: '8px 16px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
          >
            + Log Burn
          </button>
          <button
            onClick={() => {
              const el = document.getElementById('biochar-section');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            style={{ padding: '8px 14px', background: 'transparent', color: '#4ade80', border: '1px solid #4ade80', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
          >
            My Kilns &amp; Burns ↓
          </button>
        </div>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>{t('dashboard.overview')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            <KPICard title={t('dashboard.farmers')} value={kpis?.farmers || 0} />
            <KPICard title={t('plots.title')} value={kpis?.plots || 0} />
            <KPICard title={t('dashboard.crops')} value={kpis?.crops || 0} />
            <KPICard title={t('biochar.title')} value={kpis?.biochar || 0} />
            <KPICard title={t('soilSamples.title')} value={kpis?.soil || 0} />
          </div>
        </section>

        {/* PLOT NUDGE BANNER - shown when farmer has no plots registered */}
        {plotCount === 0 && (
          <section style={{ marginBottom: '40px' }}>
            <div style={{
              background: 'linear-gradient(135deg, #78350f, #92400e)',
              border: '2px solid #f59e0b',
              borderRadius: '12px',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '32px' }}>⚠️</span>
                <div>
                  <div style={{ color: '#fde68a', fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
                    You have not registered your farm plot yet
                  </div>
                  <div style={{ color: '#fcd34d', fontSize: '14px' }}>
                    Farm plot GPS is required to become eligible for carbon credits. Register your plot now — it only takes 2 minutes.
                  </div>
                </div>
              </div>
              <a
                href="#plots"
                onClick={() => {
                  const el = document.getElementById('plot-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  padding: '12px 24px',
                  background: '#f59e0b',
                  color: '#0f2419',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap'
                }}
              >
                + Register Farm Plot
              </a>
            </div>
          </section>
        )}

        {/* CARBON DASHBOARD - Only shown to Track A farmers (offset participants) */}
        {!carbonLoading && isOffsetParticipant && (
          <section style={{ marginBottom: '40px' }}>
            <CarbonDashboard />
          </section>
        )}

        <section style={{ marginBottom: '40px' }}>
         <OnboardingForm />
        </section>

        <section id="plot-section" style={{ marginBottom: '40px' }}>
         <PlotManagement />
        </section>

        <section style={{ marginBottom: '40px' }}>
          <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45' }}>
            <SoilUpload compact={false} />
          </div>
        </section>

        {/* ========== BIOCHAR TO SOIL SECTION ========== */}
        <section id="biochar-section" style={{ marginBottom: '40px' }}>
          <div style={{ background: '#1a3327', padding: '24px', borderRadius: '12px', border: '2px solid #4ade80' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h2 style={{ margin: 0, color: '#4ade80', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  🔥 {t('biocharToSoil.title')}
                  <span style={{ fontSize: '12px', background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: '12px' }}>
                    {t('biocharToSoil.carbonCredits')}
                  </span>
                </h2>
                <p style={{ margin: '8px 0 0 0', color: '#888', fontSize: '14px' }}>
                  {t('biocharToSoil.subtitle')}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowKilnForm(true)}
                  style={{
                    padding: '10px 20px',
                    background: '#4ade80',
                    color: '#0f2419',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  {t('biocharToSoil.quickActions.registerKiln')}
                </button>
                {kilns.length > 0 && (
                  <button
                    onClick={() => {
                      setSelectedKiln(kilns[0]);
                      setShowBurnForm(true);
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    {t('biocharToSoil.quickActions.logBurn')}
                  </button>
                )}
              </div>
            </div>

            {/* Biochar Tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {['overview', 'kilns', 'burns'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setBiocharTab(tab)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    background: biocharTab === tab ? '#4ade80' : '#0f2419',
                    color: biocharTab === tab ? '#0f2419' : '#888'
                  }}
                >
                  {t(`biocharToSoil.tabs.${tab}`)}
                </button>
              ))}
            </div>

            {biocharLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>{t('common.loading')}</div>
            ) : (
              <>
                {/* Overview Tab */}
                {biocharTab === 'overview' && (
                  <div>
                    {/* Stats */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                      <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4ade80' }}>{kilns.length}</div>
                        <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase' }}>{t('biocharToSoil.stats.totalKilns')}</div>
                      </div>
                      <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#22c55e' }}>{verifiedKilns}</div>
                        <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase' }}>{t('biocharToSoil.stats.verified')}</div>
                      </div>
                      <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>{completedBurns}</div>
                        <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase' }}>{t('biocharToSoil.stats.completedBurns')}</div>
                      </div>
                      <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>{totalCredits.toFixed(2)}</div>
                        <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase' }}>{t('biocharToSoil.stats.creditsEarned')}</div>
                      </div>
                      {/* Estimated Earnings removed per Michael's request - no revenue promises */}
                    </div>

                    {/* The 4 Steps */}
                    <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#4ade80', fontSize: '14px' }}>{t('biocharToSoil.howItWorks.title')}</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                        {[
                          { step: 1, title: t('biocharToSoil.howItWorks.step1.title'), desc: t('biocharToSoil.howItWorks.step1.short') },
                          { step: 2, title: t('biocharToSoil.howItWorks.step2.title'), desc: t('biocharToSoil.howItWorks.step2.short') },
                          { step: 3, title: t('biocharToSoil.howItWorks.step3.title'), desc: t('biocharToSoil.howItWorks.step3.short') },
                          { step: 4, title: t('biocharToSoil.howItWorks.step4.title'), desc: t('biocharToSoil.howItWorks.step4.short') }
                        ].map(item => (
                          <div key={item.step} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '28px', height: '28px', borderRadius: '50%',
                              background: '#4ade80', color: '#0f2419',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontWeight: 'bold', fontSize: '12px', flexShrink: 0
                            }}>{item.step}</div>
                            <div>
                              <div style={{ color: 'white', fontSize: '13px', fontWeight: '500' }}>{item.title}</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>{item.desc}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Quick Start / Empty State */}
                    {kilns.length === 0 && (
                      <div style={{ background: '#0f2419', padding: '24px', borderRadius: '8px', textAlign: 'center' }}>
                        <p style={{ color: '#888', marginBottom: '16px' }}>
                          {t('biocharToSoil.kilns.noKilnsDesc')}
                        </p>
                        <button
                          onClick={() => setShowKilnForm(true)}
                          style={{
                            padding: '12px 24px',
                            background: '#4ade80',
                            color: '#0f2419',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          {t('biocharToSoil.kilns.registerFirst')}
                        </button>
                      </div>
                    )}

                    {/* Recent Burns */}
                    {burns.length > 0 && (
                      <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px' }}>
                        <h4 style={{ margin: '0 0 12px 0', color: '#4ade80', fontSize: '14px' }}>{t('biocharToSoil.recentBurns.title')}</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {burns.slice(0, 3).map(burn => {
                            let steps = [];
                            try { steps = JSON.parse(burn.steps_completed || '[]'); } catch (e) { steps = []; }
                            return (
                              <div key={burn.burn_id} style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '10px', background: '#1a3327', borderRadius: '6px'
                              }}>
                                <div>
                                  <span style={{ color: '#4ade80', fontWeight: '500' }}>{burn.burn_id}</span>
                                  <span style={{ color: '#888', marginLeft: '10px', fontSize: '12px' }}>{burn.burn_date}</span>
                                </div>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  {[1,2,3,4].map(s => (
                                    <div key={s} style={{
                                      width: '20px', height: '20px', borderRadius: '50%',
                                      background: steps.includes(`step${s}`) ? '#4ade80' : '#2d5a45',
                                      color: steps.includes(`step${s}`) ? '#0f2419' : '#888',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: '10px', fontWeight: 'bold'
                                    }}>{s}</div>
                                  ))}
                                  <span style={{
                                    marginLeft: '8px', padding: '2px 8px', borderRadius: '10px',
                                    fontSize: '10px', fontWeight: 'bold',
                                    background: burn.status === 'complete' ? '#22c55e' : '#f59e0b',
                                    color: 'white'
                                  }}>{burn.status === 'complete' ? t('biocharToSoil.status.complete') : t('biocharToSoil.status.incomplete')}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Kilns Tab */}
                {biocharTab === 'kilns' && (
                  <div>
                    {kilns.length === 0 ? (
                      <div style={{ background: '#0f2419', padding: '24px', borderRadius: '8px', textAlign: 'center' }}>
                        <p style={{ color: '#888' }}>{t('biocharToSoil.kilns.noKilns')}</p>
                        <button
                          onClick={() => setShowKilnForm(true)}
                          style={{ padding: '12px 24px', background: '#4ade80', color: '#0f2419', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginTop: '12px' }}
                        >
                          {t('biocharToSoil.kilns.registerFirst')}
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {kilns.map(kiln => (
                          <div key={kiln.kiln_id} style={{ background: '#0f2419', padding: '16px', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                  <span style={{ color: '#4ade80', fontWeight: 'bold' }}>{kiln.kiln_id}</span>
                                  <span style={{
                                    padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold',
                                    background: kiln.status === 'verified' ? '#22c55e' : '#f59e0b',
                                    color: 'white'
                                  }}>{kiln.status === 'verified' ? t('biocharToSoil.stats.verified') : t('biocharToSoil.status.pending')}</span>
                                </div>
                                <div style={{ color: '#888', fontSize: '12px' }}>
                                  {kiln.kiln_type} • {t('biocharToSoil.kilns.registered')} {kiln.registration_date}
                                </div>
                                <div style={{ color: '#888', fontSize: '11px', marginTop: '4px', fontFamily: 'monospace' }}>
                                  GPS: {kiln.gps_lat?.toFixed(4)}, {kiln.gps_lng?.toFixed(4)}
                                </div>
                              </div>
                              <button
                                onClick={() => { setSelectedKiln(kiln); setShowBurnForm(true); }}
                                style={{ padding: '8px 16px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}
                              >
                                {t('biocharToSoil.kilns.logBurn')}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Burns Tab */}
                {biocharTab === 'burns' && (
                  <div>
                    {burns.length === 0 ? (
                      <div style={{ background: '#0f2419', padding: '24px', borderRadius: '8px', textAlign: 'center' }}>
                        <p style={{ color: '#888' }}>{t('biocharToSoil.burns.noBurns')}</p>
                        {kilns.length > 0 && (
                          <button
                            onClick={() => { setSelectedKiln(kilns[0]); setShowBurnForm(true); }}
                            style={{ padding: '12px 24px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginTop: '12px' }}
                          >
                            {t('biocharToSoil.burns.logFirst')}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {burns.map(burn => {
                          let steps = [];
                          try { steps = JSON.parse(burn.steps_completed || '[]'); } catch (e) { steps = []; }
                          return (
                            <div key={burn.burn_id} style={{ background: '#0f2419', padding: '16px', borderRadius: '8px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                    <span style={{ color: '#4ade80', fontWeight: 'bold' }}>{burn.burn_id}</span>
                                    <span style={{
                                      padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold',
                                      background: burn.status === 'complete' ? '#22c55e' : '#f59e0b',
                                      color: 'white'
                                    }}>{burn.status === 'complete' ? t('biocharToSoil.status.complete') : t('biocharToSoil.status.incomplete')}</span>
                                  </div>
                                  <div style={{ color: '#888', fontSize: '12px' }}>
                                    {burn.burn_date} • {burn.kiln_id}
                                  </div>
                                  <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                                    {burn.biomass_quantity} {burn.biomass_unit} {t('biocharToSoil.burns.of')} {burn.biomass_type}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    {[1,2,3,4].map(s => (
                                      <div key={s} style={{
                                        width: '28px', height: '28px', borderRadius: '50%',
                                        background: steps.includes(`step${s}`) ? '#4ade80' : '#2d5a45',
                                        color: steps.includes(`step${s}`) ? '#0f2419' : '#888',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '12px', fontWeight: 'bold'
                                      }}>{steps.includes(`step${s}`) ? '✓' : s}</div>
                                    ))}
                                  </div>
                                  {burn.status !== 'complete' && (
                                    <button
                                      onClick={() => {
                                        const kiln = kilns.find(k => k.kiln_id === burn.kiln_id) || kilns[0];
                                        setSelectedKiln(kiln);
                                        setSelectedBurn(burn);
                                        setShowBurnForm(true);
                                      }}
                                      style={{
                                        padding: '6px 14px',
                                        background: '#f59e0b',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '12px'
                                      }}
                                    >
                                      ▶ Continue
                                    </button>
                                  )}
                                  {burn.credits_earned > 0 && (
                                    <div style={{ color: '#4ade80', fontWeight: 'bold', fontSize: '14px' }}>
                                      {(burn.credits_earned || 0).toFixed(2)} {t('biocharToSoil.burns.details.credits').toLowerCase()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Earnings Tab removed per Michael's request - no revenue promises */}
              </>
            )}
          </div>
        </section>
        {/* ========== END BIOCHAR TO SOIL SECTION ========== */}

        <section style={{ marginBottom: '40px' }}>
          <BiocharResources />
        </section>

        {/* OFFSETS PLACEHOLDER - Only shown to Track B farmers (non-offset participants) */}
        {!carbonLoading && !isOffsetParticipant && (
          <section style={{ marginBottom: '40px' }}>
            <OffsetsPlaceholder2026 />
          </section>
        )}

        <section style={{ marginBottom: '40px' }}>
          <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45' }}>
            <h2 style={{ marginTop: 0, color: '#4ade80' }}>Soil Health Resources</h2>
            <p style={{ color: '#888', marginBottom: '16px' }}>
              Comprehensive guides on maintaining and improving soil health through regenerative practices.
            </p>
            <div style={{ background: '#0f2419', padding: '16px', borderRadius: '4px', border: '1px solid #2d5a45' }}>
              <p style={{ color: '#4ade80', margin: 0, fontStyle: 'italic' }}>
                Coming Soon: Soil testing guides, pH management, organic matter improvement, and erosion prevention techniques.
              </p>
            </div>
          </div>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45' }}>
            <h2 style={{ marginTop: 0, color: '#4ade80' }}>Farmer Resources</h2>
            <p style={{ color: '#888', marginBottom: '16px' }}>
              Agricultural best practices, tools, and resources to support your farming journey.
            </p>
            <div style={{ background: '#0f2419', padding: '16px', borderRadius: '4px', border: '1px solid #2d5a45' }}>
              <p style={{ color: '#4ade80', margin: 0, fontStyle: 'italic' }}>
                Coming Soon: Crop rotation guides, pest management, water conservation, and market access resources.
              </p>
            </div>
          </div>
        </section>

        {/* YOUTH OPPORTUNITIES - With John Lewis Fellowship */}
        <section style={{ marginBottom: '40px' }}>
          <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45' }}>
            <h2 style={{ marginTop: 0, color: '#4ade80' }}>Youth Opportunities</h2>
            <p style={{ color: '#888', marginBottom: '16px' }}>
              Programs and opportunities for young farmers and agricultural entrepreneurs.
            </p>
            
            {/* John Lewis Fellowship */}
            <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '18px' }}>John Lewis Young Leaders Fellowship</h3>
                  <p style={{ color: '#888', margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>
                    A prestigious fellowship program for young leaders committed to social change and community development. Open to emerging leaders in agriculture, sustainability, and rural development.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Fellowship</span>
                    <span style={{ background: '#3b82f6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Young Leaders</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <a 
                    href="https://www.linkedin.com/pulse/john-lewis-young-leaders-fellowship-pvube/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      padding: '10px 20px', 
                      background: '#4ade80', 
                      color: '#0f2419', 
                      textDecoration: 'none', 
                      borderRadius: '6px', 
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    Learn More
                  </a>
                </div>
              </div>
            </div>

            {/* 4 Africa Conservation Opportunities */}
            <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '18px' }}>4 Africa Conservation Opportunities</h3>
                  <p style={{ color: '#888', margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>
                    Discover conservation programs and opportunities across Africa. Includes internships, fellowships, and career paths in wildlife conservation, environmental protection, and sustainable land management.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Conservation</span>
                    <span style={{ background: '#f59e0b', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Africa</span>
                    <span style={{ background: '#8b5cf6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Multiple Programs</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <a 
                    href="https://www.linkedin.com/pulse/4-africa-conservation-opportunities-auy2e/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      padding: '10px 20px', 
                      background: '#4ade80', 
                      color: '#0f2419', 
                      textDecoration: 'none', 
                      borderRadius: '6px', 
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    Learn More
                  </a>
                </div>
              </div>
            </div>

            {/* MCW Global Leadership Program */}
            <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '18px' }}>MCW Global Leadership Program</h3>
                  <p style={{ color: '#888', margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>
                    Step into leadership with the MCW Global Young Leaders Access Program. One month left to apply! A transformative opportunity for emerging leaders committed to creating positive change in their communities.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#ec4899', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Leadership</span>
                    <span style={{ background: '#f59e0b', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Apply Soon</span>
                    <span style={{ background: '#3b82f6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Global</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <a 
                    href="https://www.linkedin.com/posts/hikaruhayakawa_one-month-left-to-apply-step-into-leadership-activity-7416531947985727488-YJny/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      padding: '10px 20px', 
                      background: '#4ade80', 
                      color: '#0f2419', 
                      textDecoration: 'none', 
                      borderRadius: '6px', 
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    Learn More
                  </a>
                </div>
              </div>
            </div>

            {/* Fully Funded Masters Scholarships 2026 */}
            <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '18px' }}>Fully Funded Masters Scholarships 2026</h3>
                  <p style={{ color: '#888', margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>
                    Discover fully funded master's degree opportunities at top universities worldwide including Gates Cambridge, Chevening, Fulbright, DAAD, and more. Scholarships cover tuition, living expenses, health insurance, and travel allowances.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#8b5cf6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Scholarship</span>
                    <span style={{ background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Fully Funded</span>
                    <span style={{ background: '#3b82f6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Masters</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <a 
                    href="https://www.linkedin.com/pulse/fully-funded-masters-scholarship-2026-zpsje/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      padding: '10px 20px', 
                      background: '#4ade80', 
                      color: '#0f2419', 
                      textDecoration: 'none', 
                      borderRadius: '6px', 
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    Learn More
                  </a>
                </div>
              </div>
            </div>

            {/* Club of Rome Communications Fellowship 2026 */}
            <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '18px' }}>Club of Rome Communications Fellowship 2026</h3>
                  <p style={{ color: '#888', margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>
                    A seven-month remote mentoring programme for early-career communications professionals from Africa, Latin America, Caribbean, Middle East, and Asia-Pacific. Gain experience in communicating sustainability and systems thinking. Includes stipend and travel costs.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Fellowship</span>
                    <span style={{ background: '#3b82f6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Remote</span>
                    <span style={{ background: '#8b5cf6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Paid</span>
                    <span style={{ background: '#f59e0b', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Deadline: Feb 20, 2026</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <a 
                    href="https://www.clubofrome.org/vacancy/communications-fellowship-2026/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      padding: '10px 20px', 
                      background: '#4ade80', 
                      color: '#0f2419', 
                      textDecoration: 'none', 
                      borderRadius: '6px', 
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    Apply Now
                  </a>
                </div>
              </div>
            </div>

            {/* Sir Cyril Taylor Young African Leaders Program 2026 */}
            <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '18px' }}>Sir Cyril Taylor Young African Leaders Program 2026</h3>
                  <p style={{ color: '#888', margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>
                    Fully funded leadership program for young changemakers (18-35) from Sub-Saharan Africa. Includes trip to Geneva, Switzerland, Global Competence Certificate, seed funding for community projects, and mentorship.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Fully Funded</span>
                    <span style={{ background: '#3b82f6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Geneva, Switzerland</span>
                    <span style={{ background: '#8b5cf6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Ages 18-35</span>
                    <span style={{ background: '#f59e0b', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Deadline: Feb 20, 2026</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <a 
                    href="https://www.youthassembly.org/2025/12/24/2026-ctcf-young-african-leaders/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      padding: '10px 20px', 
                      background: '#4ade80', 
                      color: '#0f2419', 
                      textDecoration: 'none', 
                      borderRadius: '6px', 
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    Apply Now
                  </a>
                </div>
              </div>
            </div>

            {/* UNEP Young Champions of the Earth 2026 */}
            <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '18px' }}>UNEP Young Champions of the Earth 2026</h3>
                  <p style={{ color: '#888', margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>
                    The UN Environment Programme's flagship youth initiative recognizes innovators aged 18–30 tackling climate change, biodiversity loss, and pollution. Winners receive $10,000 seed funding, mentorship, training, access to UN experts, and a chance to pitch for a $100,000 Planet A grant. Must have at least 6 months' experience working on an environmental solution.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>$10,000 + $100K Pitch</span>
                    <span style={{ background: '#3b82f6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Global</span>
                    <span style={{ background: '#8b5cf6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Ages 18-30</span>
                    <span style={{ background: '#f59e0b', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Deadline: March 31, 2026</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <a 
                    href="https://www.unep.org/youngchampions/apply/timeline" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      padding: '10px 20px', 
                      background: '#4ade80', 
                      color: '#0f2419', 
                      textDecoration: 'none', 
                      borderRadius: '6px', 
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    Apply Now
                  </a>
                </div>
              </div>
            </div>

            {/* HACU Coca-Cola First Generation Scholarship */}
            <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '18px' }}>HACU Coca-Cola First Generation Scholarship</h3>
                  <p style={{ color: '#888', margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>
                    The Hispanic Association of Colleges and Universities is partnering with The Coca-Cola Foundation to offer 17 scholarships of $5,000 each for eligible first-generation college students. Open to graduating high school seniors (2025-2026) and current college students with a minimum 3.0 GPA attending HACU-member institutions.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#8b5cf6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Scholarship</span>
                    <span style={{ background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>$5,000</span>
                    <span style={{ background: '#3b82f6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>First Generation</span>
                    <span style={{ background: '#f59e0b', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Deadline: May 15, 2026</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <a 
                    href="https://hacu.net/hacu-is-accepting-applications-for-the-coca-cola-first-generation-scholarships-2/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      padding: '10px 20px', 
                      background: '#4ade80', 
                      color: '#0f2419', 
                      textDecoration: 'none', 
                      borderRadius: '6px', 
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    Apply Now
                  </a>
                </div>
              </div>
            </div>

            {/* UN Climate Catalysts Mentorship Programme 3.0 */}
            <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '18px' }}>UN Climate Catalysts Mentorship Programme 3.0</h3>
                  <p style={{ color: '#888', margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>
                    Brought to you by UN Climate Change's Youth4Capacity programme and UNDP's Youth4Climate initiative, supported by the Italian Ministry of Environment. An intensive mentorship programme for young climate leaders to transform ideas into concrete action. Includes three online bootcamps, live sessions with global experts, and the opportunity to attend COP31 and pitch on a global stage.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#3b82f6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Mentorship</span>
                    <span style={{ background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>UN Programme</span>
                    <span style={{ background: '#8b5cf6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>COP31 Opportunity</span>
                    <span style={{ background: '#f59e0b', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Applications Open</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <a
                    href="https://lnkd.in/dDq4EPPD"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '10px 20px',
                      background: '#4ade80',
                      color: '#0f2419',
                      textDecoration: 'none',
                      borderRadius: '6px',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    Apply Now
                  </a>
                </div>
              </div>
            </div>

            {/* EARN Demonstration Farm Grant */}
            <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '18px' }}>EARN Demonstration Farm Grant</h3>
                  <p style={{ color: '#888', margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>
                    Earth Allies Restoration Network (EARN) offers grants to East African organizations for restoration agriculture. Includes a 10-day Permaculture Design Certification course, a site visit by an EARN trainer, $3,000 seed grant in year one, and up to $5,000/year for four more years (up to $23,000 over five years). Open to NGOs/CBOs in Uganda, Kenya, Tanzania, Rwanda, or Malawi.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Grant</span>
                    <span style={{ background: '#f59e0b', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Up to $23,000</span>
                    <span style={{ background: '#3b82f6', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>East Africa</span>
                    <span style={{ background: '#ec4899', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>Deadline: March 31, 2026</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <a 
                    href="https://earnglobal.earth/grant-details/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      padding: '10px 20px', 
                      background: '#4ade80', 
                      color: '#0f2419', 
                      textDecoration: 'none', 
                      borderRadius: '6px', 
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    Apply Now
                  </a>
                </div>
              </div>
            </div>

            {/* ========== FREE PROGRAMS FOR FUTURE DIPLOMATS ========== */}
            <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2419 100%)', padding: '24px', borderRadius: '12px', border: '2px solid #3b82f6', marginBottom: '16px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#60a5fa', fontSize: '20px', textAlign: 'center' }}>
                🌍 Free Programs for Future Diplomats
              </h3>
              <p style={{ color: '#94a3b8', fontSize: '14px', textAlign: 'center', marginBottom: '20px' }}>
                Fully funded international programs, fellowships, and exchanges for young leaders in diplomacy, peace, and global leadership.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                {/* Erasmus+ Youth Exchanges */}
                <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <span style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>Erasmus+ Youth Exchanges</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ background: '#3b82f6', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Europe</span>
                      <span style={{ background: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Fully Funded</span>
                    </div>
                  </div>
                  <a href="https://youth.europa.eu/go-abroad/volunteering" target="_blank" rel="noopener noreferrer" style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    Apply
                  </a>
                </div>

                {/* European Solidarity Corps */}
                <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <span style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>European Solidarity Corps</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ background: '#3b82f6', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Europe</span>
                      <span style={{ background: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Travel + Living Covered</span>
                    </div>
                  </div>
                  <a href="https://youth.europa.eu/solidarity_en" target="_blank" rel="noopener noreferrer" style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    Apply
                  </a>
                </div>

                {/* UN Volunteer Online */}
                <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <span style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>UN Volunteer Online Opportunities</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ background: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Global</span>
                      <span style={{ background: '#8b5cf6', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Remote</span>
                    </div>
                  </div>
                  <a href="https://www.onlinevolunteering.org/en" target="_blank" rel="noopener noreferrer" style={{ padding: '8px 16px', background: '#22c55e', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    Apply
                  </a>
                </div>

                {/* OSCE Scholarship */}
                <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <span style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>OSCE Scholarship for Peace & Security</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ background: '#3b82f6', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Europe & Central Asia</span>
                      <span style={{ background: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Fully Funded Online</span>
                    </div>
                  </div>
                  <a href="https://www.osce.org/scholarship" target="_blank" rel="noopener noreferrer" style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    Apply
                  </a>
                </div>

                {/* UNAOC Fellowship */}
                <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <span style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>UNAOC Fellowship Programme</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ background: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Global</span>
                      <span style={{ background: '#f59e0b', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>All Expenses Paid</span>
                    </div>
                  </div>
                  <a href="https://www.unaoc.org/what-we-do/fellowship/" target="_blank" rel="noopener noreferrer" style={{ padding: '8px 16px', background: '#22c55e', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    Apply
                  </a>
                </div>

                {/* Atlantic Dialogues */}
                <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <span style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>Atlantic Dialogues Emerging Leaders</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ background: '#f59e0b', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Morocco</span>
                      <span style={{ background: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Fully Funded</span>
                    </div>
                  </div>
                  <a href="https://www.atlanticdialogues.org/" target="_blank" rel="noopener noreferrer" style={{ padding: '8px 16px', background: '#f59e0b', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    Apply
                  </a>
                </div>

                {/* European Forum Alpbach */}
                <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <span style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>European Forum Alpbach Scholarships</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ background: '#3b82f6', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Austria</span>
                      <span style={{ background: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Board & Conference Fees</span>
                    </div>
                  </div>
                  <a href="https://www.alpbach.org/en/" target="_blank" rel="noopener noreferrer" style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    Apply
                  </a>
                </div>

                {/* Humanity in Action */}
                <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <span style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>Humanity in Action Fellowship</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ background: '#3b82f6', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Europe & US</span>
                      <span style={{ background: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Fully Funded</span>
                    </div>
                  </div>
                  <a href="https://www.humanityinaction.org/" target="_blank" rel="noopener noreferrer" style={{ padding: '8px 16px', background: '#22c55e', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    Apply
                  </a>
                </div>

                {/* GCSP Youth Connect */}
                <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <span style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>GCSP Youth Connect Programme</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ background: '#ec4899', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Switzerland</span>
                      <span style={{ background: '#8b5cf6', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Free Webinars</span>
                    </div>
                  </div>
                  <a href="https://www.gcsp.ch/" target="_blank" rel="noopener noreferrer" style={{ padding: '8px 16px', background: '#ec4899', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    Apply
                  </a>
                </div>

                {/* Young European Leadership */}
                <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <span style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>Young European Leadership Conference</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ background: '#3b82f6', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Europe</span>
                      <span style={{ background: '#f59e0b', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Travel Grants Available</span>
                    </div>
                  </div>
                  <a href="https://younglead.eu/" target="_blank" rel="noopener noreferrer" style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    Apply
                  </a>
                </div>

                {/* Women Deliver */}
                <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', border: '1px solid #ec4899' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <span style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>Women Deliver Young Leaders</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ background: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Global</span>
                      <span style={{ background: '#ec4899', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Women-Led</span>
                    </div>
                  </div>
                  <a href="https://womendeliver.org/youth/" target="_blank" rel="noopener noreferrer" style={{ padding: '8px 16px', background: '#ec4899', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    Apply
                  </a>
                </div>

              </div>
            </div>
            {/* ========== END DIPLOMAT PROGRAMS ========== */}

            {/* MIT Free AI Courses Section */}
            <div style={{ background: '#0f2419', padding: '24px', borderRadius: '8px', border: '2px solid #3b82f6', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <span style={{ fontSize: '28px' }}>🎓</span>
                <div>
                  <h3 style={{ margin: '0', color: '#4ade80', fontSize: '20px' }}>MIT Free AI Courses</h3>
                  <p style={{ margin: '4px 0 0 0', color: '#888', fontSize: '13px' }}>Learn artificial intelligence from MIT — all free and online</p>
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                
                {/* AI 101 */}
                <a href="https://ocw.mit.edu/courses/res-6-013-ai-101-fall-2021/" target="_blank" rel="noopener noreferrer" style={{ background: '#1a3327', padding: '16px', borderRadius: '8px', border: '1px solid #2d5a45', textDecoration: 'none', display: 'block' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '15px' }}>1. AI 101</h4>
                  <p style={{ margin: 0, color: '#888', fontSize: '13px', lineHeight: '1.4' }}>Intro to AI concepts. Perfect for absolute beginners with no background.</p>
                </a>

                {/* Introduction to Deep Learning */}
                <a href="https://introtodeeplearning.com/" target="_blank" rel="noopener noreferrer" style={{ background: '#1a3327', padding: '16px', borderRadius: '8px', border: '1px solid #2d5a45', textDecoration: 'none', display: 'block' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '15px' }}>2. Introduction to Deep Learning</h4>
                  <p style={{ margin: 0, color: '#888', fontSize: '13px', lineHeight: '1.4' }}>Bootcamp on deep learning fundamentals with hands-on TensorFlow labs.</p>
                </a>

                {/* Artificial Intelligence */}
                <a href="https://ocw.mit.edu/courses/6-034-artificial-intelligence-fall-2010/" target="_blank" rel="noopener noreferrer" style={{ background: '#1a3327', padding: '16px', borderRadius: '8px', border: '1px solid #2d5a45', textDecoration: 'none', display: 'block' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '15px' }}>3. Artificial Intelligence</h4>
                  <p style={{ margin: 0, color: '#888', fontSize: '13px', lineHeight: '1.4' }}>Problem-solving, knowledge representation, and learning methods of AI.</p>
                </a>

                {/* Introduction to Machine Learning */}
                <a href="https://ocw.mit.edu/courses/6-036-introduction-to-machine-learning-fall-2020/" target="_blank" rel="noopener noreferrer" style={{ background: '#1a3327', padding: '16px', borderRadius: '8px', border: '1px solid #2d5a45', textDecoration: 'none', display: 'block' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '15px' }}>4. Introduction to Machine Learning</h4>
                  <p style={{ margin: 0, color: '#888', fontSize: '13px', lineHeight: '1.4' }}>Beginner-friendly ML principles, algorithms, and applications.</p>
                </a>

                {/* How to AI (Almost) Anything */}
                <a href="https://mit-mi.github.io/how2ai-course/spring2025/" target="_blank" rel="noopener noreferrer" style={{ background: '#1a3327', padding: '16px', borderRadius: '8px', border: '1px solid #2d5a45', textDecoration: 'none', display: 'block' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '15px' }}>5. How to AI (Almost) Anything</h4>
                  <p style={{ margin: 0, color: '#888', fontSize: '13px', lineHeight: '1.4' }}>Apply AI to music, art, senses, and real-world data modalities.</p>
                </a>

                {/* Understanding the World Through Data */}
                <a href="https://openlearning.mit.edu/courses-programs/mitx-courses/understanding-world-through-data" target="_blank" rel="noopener noreferrer" style={{ background: '#1a3327', padding: '16px', borderRadius: '8px', border: '1px solid #2d5a45', textDecoration: 'none', display: 'block' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '15px' }}>6. Understanding the World Through Data</h4>
                  <p style={{ margin: 0, color: '#888', fontSize: '13px', lineHeight: '1.4' }}>Explore data analysis & ML to decode real-world problems.</p>
                </a>

                {/* AI in K-12 Education */}
                <a href="https://openlearning.mit.edu/courses-programs" target="_blank" rel="noopener noreferrer" style={{ background: '#1a3327', padding: '16px', borderRadius: '8px', border: '1px solid #2d5a45', textDecoration: 'none', display: 'block' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '15px' }}>7. Generative AI in K-12 Education</h4>
                  <p style={{ margin: 0, color: '#888', fontSize: '13px', lineHeight: '1.4' }}>Foundations of generative AI technology and possibilities for education.</p>
                </a>

                {/* Introduction to Algorithms */}
                <a href="https://ocw.mit.edu/courses/6-006-introduction-to-algorithms-spring-2020/" target="_blank" rel="noopener noreferrer" style={{ background: '#1a3327', padding: '16px', borderRadius: '8px', border: '1px solid #2d5a45', textDecoration: 'none', display: 'block' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '15px' }}>8. Introduction to Algorithms</h4>
                  <p style={{ margin: 0, color: '#888', fontSize: '13px', lineHeight: '1.4' }}>Key prerequisite for efficient AI work — data structures & algorithms.</p>
                </a>

                {/* Foundation Models and Generative AI */}
                <a href="https://ocw.mit.edu/courses/6-s087-foundation-models-and-generative-ai-january-iap-2024/" target="_blank" rel="noopener noreferrer" style={{ background: '#1a3327', padding: '16px', borderRadius: '8px', border: '1px solid #2d5a45', textDecoration: 'none', display: 'block' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#4ade80', fontSize: '15px' }}>9. Foundation Models & Generative AI</h4>
                  <p style={{ margin: 0, color: '#888', fontSize: '13px', lineHeight: '1.4' }}>Overview of modern AI like ChatGPT, DALL-E, and how they work.</p>
                </a>

              </div>

              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <a 
                  href="https://openlearning.mit.edu/news/13-foundational-ai-courses-resources-mit" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    display: 'inline-block',
                    padding: '10px 24px', 
                    background: '#3b82f6', 
                    color: 'white', 
                    textDecoration: 'none', 
                    borderRadius: '6px', 
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  View All MIT AI Courses →
                </a>
              </div>
            </div>

            {/* Coming Soon placeholder */}
            <div style={{ background: '#0f2419', padding: '16px', borderRadius: '4px', border: '1px solid #2d5a45' }}>
              <p style={{ color: '#4ade80', margin: 0, fontStyle: 'italic' }}>
                More Coming Soon: Training programs, mentorship opportunities, startup resources, and funding for young farmers.
              </p>
            </div>
          </div>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
              <div>
                <h3 style={{ color: '#4ade80' }}>{t('dashboard.communityPrograms')}</h3>
                <p style={{ color: '#888' }}>{t('dashboard.communityDesc')}</p>
              </div>
              <div>
                <h3 style={{ color: '#4ade80' }}>{t('dashboard.partnerDirectory')}</h3>
                <p style={{ color: '#888' }}>{t('dashboard.partnerDesc')}</p>
              </div>
              <div>
                <h3 style={{ color: '#4ade80' }}>Learning & Global Initiatives</h3>
                <p style={{ color: '#888', marginBottom: '12px' }}>Connect with global thought leaders on education, sustainability, and systems change.</p>
                <a 
                  href="https://www.linkedin.com/company/clubofrome/posts/?feedView=all" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    display: 'inline-block',
                    padding: '8px 16px', 
                    background: '#4ade80', 
                    color: '#0f2419', 
                    textDecoration: 'none', 
                    borderRadius: '4px', 
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  Club of Rome - Learning Planet
                </a>
              </div>
            </div>
          </div>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <div style={{ background: '#1a3327', padding: '32px', borderRadius: '12px', border: '2px solid #2d5a45' }}>
            
            <div style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', padding: '20px', borderRadius: '50px', marginBottom: '24px', textAlign: 'center' }}>
              <h1 style={{ margin: 0, color: '#2e7d32', fontSize: '36px', fontWeight: 'bold' }}>
                AgroReGenerations Microgrants
              </h1>
            </div>

            <p style={{ color: '#4ade80', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px', textAlign: 'center' }}>
              Discover small grants and funding opportunities that support your ideas in regenerative agriculture, community resilience, and climate action. Whether you are starting a soil health project, tree planting initiative, or a women-led regenerative enterprise, these microgrants can help you take the next step.
            </p>

            <div style={{ marginBottom: '40px' }}>
              <h2 style={{ color: '#4ade80', textAlign: 'center', fontSize: '32px', marginBottom: '24px' }}>
                Featured Microgrants
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                <div style={{ background: '#e8f5e9', padding: '20px 32px', borderRadius: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                  <div style={{ flex: '1 1 200px' }}>
                    <span style={{ color: '#2e7d32', fontSize: '20px', fontWeight: 'bold' }}>The Pollination Project</span>
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <span style={{ color: '#2e7d32', fontSize: '18px', fontWeight: 'bold' }}>USD 1000</span>
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <span style={{ color: '#2e7d32', fontSize: '16px' }}>Deadline: No deadline</span>
                  </div>
                  <div style={{ flex: '0 0 auto', display: 'flex', gap: '12px' }}>
                    <a href="https://thepollinationproject.org/pre-screen-quiz/" target="_blank" rel="noopener noreferrer" style={{ padding: '10px 20px', background: 'transparent', color: '#2e7d32', border: '2px solid #2e7d32', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold' }}>
                      View Grant Details
                    </a>
                    <a href="https://thepollinationproject.org/pre-screen-quiz/" target="_blank" rel="noopener noreferrer" style={{ padding: '10px 20px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold' }}>
                      Apply
                    </a>
                  </div>
                </div>

                <div style={{ background: '#e8f5e9', padding: '20px 32px', borderRadius: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                  <div style={{ flex: '1 1 200px' }}>
                    <span style={{ color: '#2e7d32', fontSize: '20px', fontWeight: 'bold' }}>Awesome Foundation</span>
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <span style={{ color: '#2e7d32', fontSize: '18px', fontWeight: 'bold' }}>USD 500-1500</span>
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <span style={{ color: '#2e7d32', fontSize: '16px' }}>Deadline: No deadline</span>
                  </div>
                  <div style={{ flex: '0 0 auto', display: 'flex', gap: '12px' }}>
                    <a href="https://www.awesomefoundation.org/" target="_blank" rel="noopener noreferrer" style={{ padding: '10px 20px', background: 'transparent', color: '#2e7d32', border: '2px solid #2e7d32', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold' }}>
                      View Grant Details
                    </a>
                    <a href="https://www.awesomefoundation.org/en/submissions/new" target="_blank" rel="noopener noreferrer" style={{ padding: '10px 20px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold' }}>
                      Apply
                    </a>
                  </div>
                </div>

                <div style={{ background: '#fce4ec', padding: '20px 32px', borderRadius: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', border: '2px solid #f06292' }}>
                  <div style={{ flex: '1 1 200px' }}>
                    <span style={{ color: '#c2185b', fontSize: '20px', fontWeight: 'bold' }}>Women in Tech - SHARE Program</span>
                    <div style={{ marginTop: '4px' }}>
                      <span style={{ color: '#c2185b', fontSize: '13px', background: '#f8bbd0', padding: '2px 8px', borderRadius: '12px' }}>Women-Led</span>
                    </div>
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <span style={{ color: '#c2185b', fontSize: '18px', fontWeight: 'bold' }}>Fellowship Program</span>
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <span style={{ color: '#c2185b', fontSize: '16px' }}>Deadline: Check Post</span>
                  </div>
                  <div style={{ flex: '0 0 auto', display: 'flex', gap: '12px' }}>
                    <a href="https://www.linkedin.com/posts/senegal-togo-mozambique-share-7406695540815724545-pHG1" target="_blank" rel="noopener noreferrer" style={{ padding: '10px 20px', background: 'transparent', color: '#c2185b', border: '2px solid #c2185b', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold' }}>
                      View Details
                    </a>
                    <a href="https://www.linkedin.com/posts/senegal-togo-mozambique-share-7406695540815724545-pHG1" target="_blank" rel="noopener noreferrer" style={{ padding: '10px 20px', background: '#c2185b', color: 'white', border: 'none', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold' }}>
                      Learn More
                    </a>
                  </div>
                </div>

                {/* COP31 Fellowship 2026 */}
                <div style={{ background: '#e3f2fd', padding: '20px 32px', borderRadius: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', border: '2px solid #1976d2' }}>
                  <div style={{ flex: '1 1 200px' }}>
                    <span style={{ color: '#0d47a1', fontSize: '20px', fontWeight: 'bold' }}>COP31 Fellowship 2026 – Life of Pachamama</span>
                    <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ color: '#1565c0', fontSize: '13px', background: '#bbdefb', padding: '2px 8px', borderRadius: '12px' }}>✈️ Fully Funded</span>
                      <span style={{ color: '#1565c0', fontSize: '13px', background: '#bbdefb', padding: '2px 8px', borderRadius: '12px' }}>🌍 Global South Youth</span>
                      <span style={{ color: '#1565c0', fontSize: '13px', background: '#bbdefb', padding: '2px 8px', borderRadius: '12px' }}>🇹🇷 Türkiye</span>
                    </div>
                    <p style={{ color: '#1565c0', fontSize: '13px', marginTop: '8px', marginBottom: 0, lineHeight: '1.5' }}>
                      Empowers youth from the Global South to participate in international climate negotiations at COP31. Covers travel, accommodation, and daily stipend.
                    </p>
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <span style={{ color: '#0d47a1', fontSize: '18px', fontWeight: 'bold' }}>Fellowship Program</span>
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <span style={{ color: '#0d47a1', fontSize: '16px', fontWeight: 'bold' }}>⚠️ Deadline: 30 April 2026</span>
                  </div>
                  <div style={{ flex: '0 0 auto', display: 'flex', gap: '12px' }}>
                    <a href="https://wp.me/p23f03-cUJ" target="_blank" rel="noopener noreferrer" style={{ padding: '10px 20px', background: 'transparent', color: '#1976d2', border: '2px solid #1976d2', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold' }}>
                      View Details
                    </a>
                    <a href="https://wp.me/p23f03-cUJ" target="_blank" rel="noopener noreferrer" style={{ padding: '10px 20px', background: '#1976d2', color: 'white', border: 'none', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold' }}>
                      Apply Now
                    </a>
                  </div>
                </div>

              </div>
            </div>

            <div style={{ background: '#e8f5e9', padding: '32px', borderRadius: '24px', marginTop: '40px' }}>
              <h2 style={{ color: '#2e7d32', textAlign: 'center', fontSize: '32px', marginBottom: '24px' }}>
                How to Apply for a Microgrant
              </h2>
              <div style={{ color: '#2e7d32', fontSize: '16px', lineHeight: '1.8' }}>
                <p style={{ marginBottom: '12px' }}>
                  <strong>Step 1:</strong> Identify the right grant for your project (e.g., soil health, agroforestry, or women-led initiatives).
                </p>
                <p style={{ marginBottom: '12px' }}>
                  <strong>Step 2:</strong> View grant details to check eligibility
                </p>
                <p style={{ marginBottom: '12px' }}>
                  <strong>Step 3:</strong> Submit the application on the grant website.
                </p>
                <p style={{ marginBottom: '0' }}>
                  <strong>Step 4:</strong> Keep records and follow up.
                </p>
              </div>
            </div>

          </div>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45', textAlign: 'center' }}>
            <h3 style={{ marginTop: 0, color: '#4ade80' }}>{t('dashboard.needHelp')}</h3>
            <p style={{ color: '#888', marginBottom: '16px' }}>{t('dashboard.supportDesc')}</p>
            <a href="mailto:grow@agroregenerations.io" style={{ display: 'inline-block', padding: '12px 24px', background: '#4ade80', color: '#0f2419', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold' }}>
              {t('dashboard.contactSupport')}
            </a>
          </div>
        </section>

      </div>
      
      <InstallPrompt />

      {/* Kiln Registration Modal */}
      {showKilnForm && (
        <KilnRegistration 
          onClose={() => setShowKilnForm(false)}
          onSuccess={() => {
            setShowKilnForm(false);
            fetchBiocharData();
          }}
        />
      )}

      {/* Burn Logger Modal */}
      {showBurnForm && selectedKiln && (
        <BurnLogger 
          kiln={selectedKiln}
          existingBurn={selectedBurn}
          onClose={() => {
            setShowBurnForm(false);
            setSelectedKiln(null);
            setSelectedBurn(null);
          }}
          onSuccess={() => {
            setShowBurnForm(false);
            setSelectedKiln(null);
            setSelectedBurn(null);
            fetchBiocharData();
          }}
        />
      )}
    </div>
  );
}

function KPICard({ title, value }) {
  return (
    <div style={{ background: '#1a3327', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45' }}>
      <div style={{ fontSize: '14px', color: '#888', marginBottom: '8px' }}>{title}</div>
      <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#4ade80' }}>{value}</div>
    </div>
  );
}